// @flow
import { flow, get, isNull, memoize } from "lodash/fp"
import moment from "moment"
import { createSelector } from "reselect"
import { fromJS, List, Map, Record } from "immutable"
import type Part from "@kiwicom/frontend-app/scripts/components/Part"
import type Booking from "@kiwicom/frontend-app/scripts/containers/mutable/Booking"

import { RESERVATION_PART_CONFIG } from "booking/scenes/ReservationForm/consts/reservationConsts"
import Flight from "common/records/Flight"
import type JourneyType from "common/records/Journey" // TODO remove, it's duplicate
import Journey from "common/records/Journey"
import { bookedAtSelector } from "common/services/branding/selectors"
import { getAirlines } from "common/services/staticData"
import { getOr } from "common/utils/get"
import type { IataCode } from "mmb/records/bookingInput"
import type { FlightId } from "mmb/types/primitiveTypes"
import type { ScheduleChangeStatus } from "../../scenes/BookingScheduleChanges/consts/PropTypes"
import type { BookingInvalidateEndAction, BookingInvalidateStartAction, GetState } from "../types"
import type { SubmitForPaymentActionPayload } from "../../scenes/BookingPayment/records/PropTypes"
// eslint-disable-next-line import/no-cycle
import { submitForPayment } from "../../scenes/BookingPayment/services/paymentActions"
import { scheduleChangesConditions } from "../../scenes/BookingScheduleChanges/services/helpers"
import { mapperCreatedBookingChange } from "../mapper/services/mapperManageBookingChange"
import * as helpers from "./services/helpers"
import { SERVICES, SERVICES_STATUSES } from "mmb/consts/additionalServices"
import {
  ALTERNATIVE_STATUSES,
  ALTERNATIVE_VIEWS_TKEYS,
  ERRORS,
  MODAL_OPEN_STATUSES,
  SHOT_ACTIONS,
  SINGLE_COLUMN_WIDTH,
} from "../../scenes/BookingScheduleChanges/consts/constants"
import {
  manageScheduleChange,
  selectScheduleChangeAlternative,
} from "mmb/services/bookings/bookingsApi"
import * as bookingSelectors from "../selector"
// eslint-disable-next-line import/no-cycle
import * as bookingActions from "../index"
import { convertPriceSelector, currencySelector } from "common/services/currencies/selectors"
import { BOOKING_STATUSES, MODALS } from "mmb/consts/booking"
import {
  REASONS,
  VIEWS,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingRefund/consts/refundConsts"
// eslint-disable-next-line import/no-cycle
import {
  changeViewAction as changeRefundView,
  setReasonAction,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingRefund/services/BookingRefundDuck"
import { ACTIONS } from "mmb/scenes/AccountBooking/components/Booking/services/scheduleChanges/services/consts"
import {
  INVALIDATE_END,
  INVALIDATE_START,
  SET_MODAL_OPEN,
} from "mmb/scenes/AccountBooking/components/Booking/services/consts"

// CONSTANTS

type ScheduleChangesStateType = Map<string, any> // eslint-disable-line flowtype/no-weak-types

export const initialState = Map({
  currentViewIndex: 0,
  maxViewIndex: 1,
  flightToExchange: "",
  selectedAlternativeJourneyIndex: -1,
  previewingAlternativeJourneyIndex: -1,
  openStatus: MODAL_OPEN_STATUSES.CLOSED,
  apiError: "",
  loadingStatus: false,
  isConfirmRefundOpen: false,
  resolutionArguments: [],
  isFinalModalVisible: false,
  isViewAlernatives: false,
})

type Action = {
  type: string,
  res?: any, // eslint-disable-line flowtype/no-weak-types
  payload?: any, // eslint-disable-line flowtype/no-weak-types
  modal?: ?string,
}

export default function(state: ScheduleChangesStateType = initialState, action: Action) {
  if (!Map.isMap(state)) return initialState
  if (!action) return state

  switch (action.type) {
    case ACTIONS.LOAD_SCHEDULE_CHANGE_SUCCESS: {
      const scheduleChange = get("res", action)
      if (
        scheduleChange &&
        scheduleChange.has("resolved") &&
        !scheduleChange.hasIn(["resolved", "timestamp"])
      ) {
        return initialState
          .set("openStatus", MODAL_OPEN_STATUSES.OPEN)
          .set("isViewAlternatives", state.get("isViewAlternatives"))
      }
      return state
    }

    case ACTIONS.REQUEST_RESOLUTION_SUCCESS:
      return state.set("isFinalModalVisible", true).set("apiError", "")

    case ACTIONS.REQUEST_RESOLUTION_FAILURE:
      return state
        .set("isFinalModalVisible", true) // delete?
        .set(
          "apiError",
          get("error", action) === ERRORS.NO_SUCH_FLIGHT
            ? __("schedule_changes.errors.no_longer_available")
            : __("common.error"),
        )

    case SET_MODAL_OPEN: {
      if (action.modal === null) {
        return state.set("isFinalModalVisible", false)
      }
      return state
    }

    case ACTIONS.SET_OPEN_STATUS: // TODO remove, redundant
      return state.set("openStatus", get("payload.openStatus", action))

    case ACTIONS.SET_LOADING_STATUS: // TODO remove, redundant
      return state.set("loadingStatus", get("payload.status", action))

    case ACTIONS.SET_VIEW_ALTERNATIVES:
      return state.set("isViewAlternatives", get("payload.view", action))

    case ACTIONS.CHOOSE_PATH:
    case ACTIONS.VIEW_CHANGE:
    case ACTIONS.GO_BACK:
    case ACTIONS.CHOOSE_FLIGHT_TO_EXCHANGE:
    case ACTIONS.SELECT_ALTERNATIVE:
    case ACTIONS.PREVIEW_ALTERNATIVE:
    case ACTIONS.TOGGLE_CONFIRM_REFUND:
      return state.mergeDeep(get("payload", action))

    default:
      return state
  }
}

// SELECTORS
// ===
export const getResolutionArgs = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.scheduleChanges.get("resolutionArgs")

export const isFinalModalVisibleSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.scheduleChanges.get("isFinalModalVisible")

export const apiErrorMessageSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.scheduleChanges.get("apiError")

export const resolvedArgsSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.scheduleChanges.get("resolutionArguments")

export const loadingStatusSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.scheduleChanges.get("loadingStatus")

export const getCurrentViewIndex = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.scheduleChanges.get("currentViewIndex")

export const maxViewIndexSelector = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.scheduleChanges.get("maxViewIndex")

export const getCurrentViewSelector: $FlowFixMe = createSelector(
  getCurrentViewIndex,
  index => ALTERNATIVE_VIEWS_TKEYS[index],
)

export const getSelectedAlternativeJourneyIndex = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.scheduleChanges.get("selectedAlternativeJourneyIndex")

export const getPreviewedAlternativeJourneyIndex = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.scheduleChanges.get("previewingAlternativeJourneyIndex")

export const getFlightToBeExchanged = (
  state: any, // eslint-disable-line flowtype/no-weak-types
) => state.scheduleChanges.get("flightToExchange")

export const filterAlternativesByFlightToExchange = (
  alternatives: List<JourneyType>,
  flightID: FlightId, // TODO CR Journey -> JourneyType everywhere
) => alternatives.filter(a => a.getFlightsIds().indexOf(flightID) === -1)

export const getSelectedAlternativeJourneySelector: $FlowFixMe = createSelector(
  [bookingSelectors.scheduleChangeAlternativesSelector, getSelectedAlternativeJourneyIndex],
  (alternatives: List<JourneyType>, index) => alternatives.get(index) || Map(),
)

export const getPreviewedAlternativeJourneySelector: $FlowFixMe = createSelector(
  [bookingSelectors.scheduleChangeAlternativesSelector, getPreviewedAlternativeJourneyIndex],
  (alternatives: List<JourneyType>, index) => alternatives.get(index),
)

export const hasSelectedOrPreviewingAlternativeSelector: $FlowFixMe = createSelector(
  [getSelectedAlternativeJourneyIndex, getPreviewedAlternativeJourneyIndex],
  (selectedIndex, previewedIndex) => selectedIndex !== -1 || previewedIndex !== -1,
)

export const isScheduleChangesInProgressAlertVisibleSelector: $FlowFixMe = createSelector(
  bookingSelectors.bookingDataSelector,
  booking =>
    booking.additionalServices.find(
      change =>
        change.category === SERVICES.SCHEDULE_CHANGE &&
        (change.status === SERVICES_STATUSES.CLOSED || change.status === SERVICES_STATUSES.PENDING),
    ),
)

// Map<changes> => flightIDs[]
export const getCancelledFlights = memoize(
  // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
  flow(
    fs => fs.filter(isNull),
    fs => fs.keySeq().toArray(),
  ),
)

export const isAllMinorDelaySelector: $FlowFixMe = createSelector(
  [
    bookingSelectors.currentBookingJourneySelector,
    bookingSelectors.scheduleChangeFlightChangesSelector,
    bookingSelectors.scheduleChangeAffectedFlightsSelector,
  ],
  (
    original,
    changes: Map<string, any>,
    affectedFlights, // eslint-disable-line flowtype/no-weak-types
  ) =>
    [changes.keySeq()]
      .map(changesIDs => original.getFlights().filter(f => changesIDs.indexOf(f.id) !== -1))
      .map(fs =>
        fs.reduce((acc, f) => acc && fs.size && helpers.isMinorDelay(f, changes.get(f.id)), true),
      ) // NOTE check if reduce passes after returning 0 instead of false from isMinorDelay
      .map(allFlightsMinorDelay => allFlightsMinorDelay && affectedFlights.size === 0)[0], // minor delay also means that there is no affected flight
)

const ScheduleChangeRecord = Record({
  type: "",
  text: "",
  delay: null,
  hasAlternative: null,
  inFocus: null,
  changedTime: null,
  changedFlightNo: null,
  changedAirline: null,
  changedDepartureWhere: null,
  changedArrivalWhere: null,
})

export const getScheduleChangeAlternativeInfoSelector: $FlowFixMe = createSelector(
  [
    getPreviewedAlternativeJourneySelector,
    bookingSelectors.currentBookingJourneySelector,
    getFlightToBeExchanged,
    getCurrentViewIndex,
    bookingSelectors.scheduleChangeFlightChangesSelector,
  ],
  (
    chosenJourney: JourneyType,
    originalJourney: JourneyType,
    flightToExchange,
    currentViewIndex,
    changes,
  ) => {
    const flights = chosenJourney.getFlights()
    return chosenJourney.set(
      "trips",
      chosenJourney.trips.map(trip =>
        trip.set(
          "parts",
          trip.parts.map(part => {
            const partID = part.id

            let type
            let text
            let delay

            let changedDeparture
            let changedArrival
            let flightDuration

            const originalFlight = helpers.findFlight(flights, partID)
            const changedFlight = changes.get(partID)

            const flightDelayDuration = helpers.getFlightDelayDuration(
              originalFlight,
              changedFlight,
            )
            const hasDelay = helpers.hasFlightDelay(flightDelayDuration.value)

            if (helpers.isNewAlternative(originalJourney, partID)) {
              type = "alternative" // this is used as classname
              text = __("schedule_changes.new_alternative")
            } else if (hasDelay) {
              type = "delayed" // this is used as classname
              delay = flightDelayDuration
              changedDeparture = {
                when: {
                  utc: changedFlight.getIn(helpers.utcDeparturePath),
                  local: changedFlight.getIn(helpers.localDeparturePath),
                },
              }
              changedArrival = {
                when: {
                  utc: changedFlight.getIn(helpers.utcArrivalPath),
                  local: changedFlight.getIn(helpers.localArrivalPath),
                },
              }
              flightDuration = moment.duration(
                changedArrival.when.utc.diff(changedDeparture.when.utc),
              )
            } else {
              type = "notAffected" // this is used as classname
              text = __("schedule_changes.non_affected")
            }

            const isInFocus =
              (type === "alternative" || (flightToExchange === partID && type !== "notAffected")) &&
              currentViewIndex !== 2

            return part.set(
              "status",
              new ScheduleChangeRecord({
                delay,
                inFocus: isInFocus,
                text,
                type,
                changedTime: {
                  departure: changedDeparture,
                  arrival: changedArrival,
                  flightDuration,
                },
              }),
            )
          }),
        ),
      ),
    )
  },
)

const cancelledFlightsSelector: $FlowFixMe = createSelector(
  bookingSelectors.scheduleChangeFlightChangesSelector,
  getCancelledFlights,
)
export const hasCancelledFlightsSelector: $FlowFixMe = createSelector(
  cancelledFlightsSelector,
  (fs: Array<string>) => fs.length > 0,
)

export const isPlanAccordinglyVisibleSelector: $FlowFixMe = createSelector(
  [
    bookingSelectors.scheduleChangeFlightChangesSelector,
    hasCancelledFlightsSelector,
    bookingSelectors.scheduleChangeAffectedFlightsSelector,
  ],
  (changedFlights, hasSomeCancelledFlights, affectedFlights) =>
    changedFlights.size && !hasSomeCancelledFlights && !affectedFlights.size,
)

export const getCurrentJourneyWithScheduleChangesSelector: $FlowFixMe = createSelector(
  [
    bookingSelectors.currentBookingJourneySelector,
    getFlightToBeExchanged,
    bookingSelectors.scheduleChangeAffectedFlightsSelector,
    bookingSelectors.scheduleChangeAlternativesSelector,
    bookingSelectors.scheduleChangeFlightChangesSelector,
    bookingSelectors.currentBookingScheduleChangeSelector,
    bookingSelectors.flightsHistorySelector,
  ],
  (
    bookingJourney: JourneyType,
    flightToExchange: string,
    affectedFlights: List<string>,
    alternatives: List<JourneyType>,
    changes,
    scheduleChanges,
    flightsHistory,
  ) => {
    if (changes.isEmpty()) return null

    /*
     * If the schedule change is processed, BE will put the previous flight in flights history as a last element
     * We need this history journey, to be able to compare and display the times correctly
     * @type {Journey}
     */
    const journey = scheduleChanges.get("scheduleChangeProcessed")
      ? flightsHistory.last()
      : bookingJourney

    const flights = journey.getFlights()
    return journey.set(
      "trips",
      journey.trips.map(trip =>
        trip.set(
          "parts",
          trip.parts.map(part => {
            const partID = part.id

            let type
            let text
            let delay
            let changedFlightNo

            const originalFlight = helpers.findFlight(flights, partID)
            const changedFlight = changes.get(partID)

            let changedDeparture
            let changedArrival
            let flightDuration

            const flightDelayDuration = helpers.getFlightDelayDuration(
              originalFlight,
              changedFlight,
            )

            const hasDelay = helpers.hasFlightDelay(flightDelayDuration.value)
            const hasFlightNumberChange =
              changedFlight && changedFlight.get("number") !== originalFlight.get("number")
            const airlines = getAirlines(window.__INITIAL_STATE__)
            const changedAirline =
              changedFlight && changedFlight.get("airline")
                ? fromJS({
                    code: changedFlight.getIn(["airline", "iata"]),
                    name: changedFlight.getIn(["airline", "name"]),
                    logo: getOr(
                      RESERVATION_PART_CONFIG.flight.logo,
                      [changedFlight.getIn(["airline", "iata"]), "logo"],
                      airlines,
                    ),
                  })
                : null

            const changedDepartureWhere =
              changedFlight &&
              originalFlight.getIn(["departure", "where", "code"]) !==
                changedFlight.getIn(["departure", "where", "code"])
                ? changedFlight.getIn(["departure", "where"])
                : null
            const changedArrivalWhere =
              changedFlight &&
              originalFlight.getIn(["arrival", "where", "code"]) !==
                changedFlight.getIn(["arrival", "where", "code"])
                ? changedFlight.getIn(["arrival", "where"])
                : null

            if (getCancelledFlights(changes).includes(partID)) {
              type = "cancelled" // this is used as classname
              text = __("schedule_changes.cancelled_flight")
            } else if (affectedFlights.includes(partID) && !hasDelay) {
              type = "affected" // this is used as classname
              text = __("schedule_changes.affected_flight")
            } else if (hasFlightNumberChange && !hasDelay && changedFlight) {
              type = "flight_change" // this is used as classname
              text = __("schedule_changes.change_of_flight")
              changedFlightNo = changedFlight.get("number")
            } else if (
              (changedDepartureWhere || changedArrivalWhere) &&
              !hasDelay &&
              changedFlight
            ) {
              type = "flight_change" // this is used as classname
              text = __("schedule_changes.change_of_flight")
            } else if (hasDelay) {
              type = "delayed" // this is used as classname
              text = __("schedule_changes.intro.plan_accordingly.heading") // does not get used, so what is here should not matter
              delay = flightDelayDuration
              changedFlightNo = hasFlightNumberChange ? changedFlight.get("number") : undefined
              changedDeparture = {
                when: {
                  utc: changedFlight.getIn(helpers.utcDeparturePath),
                  local: changedFlight.getIn(helpers.localDeparturePath),
                },
              }
              changedArrival = {
                when: {
                  utc: changedFlight.getIn(helpers.utcArrivalPath),
                  local: changedFlight.getIn(helpers.localArrivalPath),
                },
              }
              flightDuration = moment.duration(
                changedArrival.when.utc.diff(changedDeparture.when.utc),
              )
            }

            // if nothing changed, return
            if (!type) return part

            const hasAlternative =
              filterAlternativesByFlightToExchange(alternatives, partID).size > 0

            const isInFocus =
              type === "alternative" || (flightToExchange === partID && type !== "notAffected")

            const changedPart = part.set(
              "status",
              new ScheduleChangeRecord({
                type,
                text,
                delay,
                hasAlternative,
                inFocus: isInFocus,
                changedFlightNo,
                changedAirline,
                changedDepartureWhere,
                changedArrivalWhere,
                changedTime: {
                  departure: changedDeparture,
                  arrival: changedArrival,
                  flightDuration,
                },
              }),
            )

            if (type === "delayed") {
              return changedPart
                .setIn(["departure", "updatedWhen"], changedFlight.getIn(["departure", "when"]))
                .setIn(["arrival", "updatedWhen"], changedFlight.getIn(["arrival", "when"]))
            }
            return changedPart
          }),
        ),
      ),
    )
  },
)

const templ = Map({
  paid: Map(),
  free: Map(),
})

const filterFreeAlternatives = as => as.filter(a => a.getIn(["prices", "default"]) === 0)
const filterPaidAlternatives = as => as.filter(a => a.getIn(["prices", "default"]) > 0)
export const hasAlternativesSelector: $FlowFixMe = createSelector(
  bookingSelectors.scheduleChangeAlternativesSelector,
  alts => alts.size > 0,
)

const sortAlternativesByMulticity = alternatives =>
  alternatives.reduce((acc, alt) => {
    const key = alt.getMulticityCities().join("-_-")

    if (acc.get(key)) {
      return acc.set(key, acc.get(key).push(alt))
    }
    return acc.set(key, List.of(alt))
  }, Map())

export const getFilteredAlternativesSelector: $FlowFixMe = createSelector(
  [
    bookingSelectors.currentBookingJourneySelector,
    bookingSelectors.scheduleChangeAlternativesSelector,
  ],
  (currentJourney: JourneyType, alternatives: List<JourneyType>) => {
    if (alternatives.size) {
      const sortedFree = sortAlternativesByMulticity(filterFreeAlternatives(alternatives))
      const sortedPaid = sortAlternativesByMulticity(filterPaidAlternatives(alternatives))

      return templ.set("free", sortedFree).set("paid", sortedPaid)
    }
    return templ
  },
)

// receives immutable list of alternatives
export const hasFreeAlternativesSelector: $FlowFixMe = createSelector(
  bookingSelectors.scheduleChangeAlternativesSelector,
  alts => filterFreeAlternatives(alts).size > 0,
)

export const isSidebarOpenSelector: $FlowFixMe = createSelector(
  [getSelectedAlternativeJourneyIndex, getFlightToBeExchanged],
  (selectedIndex, flightID) => selectedIndex === -1 && flightID !== "",
)

export const getSelectedAlternativeJourneyPriceSelector: $FlowFixMe = createSelector(
  [getSelectedAlternativeJourneySelector],
  (alternativeJourney: JourneyType) => alternativeJourney.getIn(["prices", "default"]),
)

const scheduleChangesSelector = (state: any) => state.scheduleChanges // eslint-disable-line flowtype/no-weak-types

export const scheduleChangesOpenStateSelector: $FlowFixMe = createSelector(
  scheduleChangesSelector,
  sc => sc && sc.get("openStatus"),
)

export const isConfirmRefundOpenSelector: $FlowFixMe = createSelector(
  scheduleChangesSelector,
  sc => sc.get("isConfirmRefundOpen"),
)

export const isViewAlernativesSelector: $FlowFixMe = createSelector(
  scheduleChangesSelector,
  sc => sc.get("isViewAlternatives"),
)

export const isSingleColumnSelector: $FlowFixMe = createSelector(
  state => state.window.width,
  width => width < SINGLE_COLUMN_WIDTH,
)

export const isRepairAlertVisibleSelector: $FlowFixMe = createSelector(
  bookingSelectors.bookingDataSelector,
  booking => booking.scheduleChange && booking.scheduleChange.get("action") === SHOT_ACTIONS.REPAIR,
)

// ACTION CREATORS
// ===

export const changeView = (text: string, index: number) => (
  dispatch: Function, // eslint-disable-line flowtype/no-weak-types
  getState: Function, // eslint-disable-line flowtype/no-weak-types
) => {
  const state = scheduleChangesSelector(getState())
  const maxViewIndex = state.get("maxViewIndex")

  let payload

  if (index <= maxViewIndex) {
    if (ALTERNATIVE_VIEWS_TKEYS.length - 1 !== index) {
      // if not summary then reset the selection
      payload = {
        currentViewIndex: index,
        maxViewIndex,
      }
    } else if (ALTERNATIVE_VIEWS_TKEYS.length === index) {
      payload = {
        currentViewIndex: index,
        maxViewIndex: index,
      }
    }
  }

  if (payload) {
    dispatch({
      type: ACTIONS.VIEW_CHANGE,
      payload: {
        ...payload,
        selectedAlternativeJourneyIndex: -1,
        previewingAlternativeJourneyIndex: -1,
        flightToExchange: "",
      },
    })
  }
}

export const chooseFlightToExchange = (id: string) => ({
  type: ACTIONS.CHOOSE_FLIGHT_TO_EXCHANGE,
  payload: Map({
    flightToExchange: id,
    selectedAlternativeJourneyIndex: -1,
    previewingAlternativeJourneyIndex: -1,
  }),
})

// eslint-disable-next-line flowtype/no-weak-types
export const selectAlternative = (index: number) => (dispatch: Function, getState: Function) => {
  const state = scheduleChangesSelector(getState())

  const isNew = state.get("selectedAlternativeJourneyIndex") !== index // TODO CR

  let payload

  if (isNew) {
    const nextCurrentViewIndex = state.get("currentViewIndex") + 1

    payload = Map({
      selectedAlternativeJourneyIndex: index,
      previewingAlternativeJourneyIndex: index,
      currentViewIndex: nextCurrentViewIndex,
      maxViewIndex: nextCurrentViewIndex,
    })
  } else {
    payload = Map({
      selectedAlternativeJourneyIndex: -1,
    })
  }

  dispatch({
    type: ACTIONS.SELECT_ALTERNATIVE,
    payload,
  })
}

export const previewAlternative = (index: number) => (
  dispatch: Function, // eslint-disable-line flowtype/no-weak-types
  getState: Function, // eslint-disable-line flowtype/no-weak-types
) => {
  const state = scheduleChangesSelector(getState())
  const currIndex = state.get("previewingAlternativeJourneyIndex")

  const payload = Map({
    previewingAlternativeJourneyIndex: currIndex === index ? -1 : index,
  })

  dispatch({
    type: ACTIONS.PREVIEW_ALTERNATIVE,
    payload,
  })
}

// eslint-disable-next-line flowtype/no-weak-types
export const goBack = () => (dispatch: Function, getState: Function) => {
  const state = scheduleChangesSelector(getState())
  const newIndex = state.get("currentViewIndex") - 1

  const payload = Map({
    currentViewIndex: newIndex,
    selectedAlternativeJourneyIndex: -1,
    maxViewIndex: newIndex || 1, // set new max to 1 if it's at zero
  })

  dispatch({
    type: ACTIONS.GO_BACK,
    payload,
  })
}

export const setOpenStatus = (openStatus: string) => ({
  // TODO rename openStatus to openState
  type: ACTIONS.SET_OPEN_STATUS,
  payload: {
    openStatus,
  },
})

// eslint-disable-next-line flowtype/no-weak-types
export const closeScheduleChangesModal = () => (dispatch: Function, getState: Function): * => {
  const state = getState()
  if (isFinalModalVisibleSelector(state)) {
    const bid = bookingSelectors.bidSelector(state)
    dispatch(bookingActions.reloadBooking(bid))
  }
  dispatch(bookingActions.closeModal())
}

export const setResponseError = (error: string) => ({
  type: ACTIONS.REQUEST_RESOLUTION_FAILURE,
  payload: {
    error,
  },
  _track: { error },
})

const setLoadingStatus = status => ({
  type: ACTIONS.SET_LOADING_STATUS,
  payload: { status },
})

export const scheduleChangeOrderAction = (
  serviceForPayment: SubmitForPaymentActionPayload,
) => async (dispatch: Dispatch, getState: GetState) => {
  const state = getState()

  const { payload } = serviceForPayment
  const bid = bookingSelectors.bidSelector(state)
  const changeId = bookingSelectors.currentBookingScheduleChangeSelector(state).get("id")
  const { useBalance, ...apiPayload } = payload

  const promise = selectScheduleChangeAlternative(bid, changeId, {
    ...apiPayload,
    gate: useBalance ? "credits" : "zooz",
    booked_at: bookedAtSelector(state),
  })

  const ok = await dispatch({
    type: ACTIONS.REQUEST_RESOLUTION,
    action: ACTIONS.REQUEST_RESOLUTION,
    payload: apiPayload,
    promise,
    bid,
    _track: { payload: apiPayload },
  })

  if (ok) {
    return promise.then(res => res.schedule_change.extra).then(mapperCreatedBookingChange)
  }

  return false
}

export const resolveScheduleChange = (status: ScheduleChangeStatus, toPayment?: boolean) => async (
  dispatch: Function, // eslint-disable-line flowtype/no-weak-types
  getState: Function, // eslint-disable-line flowtype/no-weak-types
) => {
  await dispatch(setLoadingStatus(true))

  const state = getState()
  const convertPrice = convertPriceSelector(state)

  const bid = bookingSelectors.bookingDataSelector(state).id
  const changeId = bookingSelectors.currentBookingScheduleChangeSelector(state).get("id")
  const alternative = getSelectedAlternativeJourneySelector(state)

  const currency = currencySelector(state)
  const eurPrice = alternative.getIn(["prices", "default"])
  const price = convertPrice(currency, eurPrice)
  const selectedFlightToBeExchanged = getFlightToBeExchanged(state)

  let payload = {}

  let _track = {
    status,
    price,
    currency,
    selectedFlightToBeExchanged,
  }

  if (status === ALTERNATIVE_STATUSES.SELECTED) {
    payload = {
      ...payload,
      trip_id: alternative.get("id"),
      price: {
        amount: eurPrice,
      },
      payment_currency: currency,
    }
  } else {
    payload = { status }
  }

  if (status === ALTERNATIVE_STATUSES.REFUNDED) {
    const refund = bookingSelectors.scheduleChangeRefundSelector(state)
    _track = {
      ..._track,
      price: refund.get("amount"),
      currency: refund.get("currency"),
    }
  }

  let promise

  if (status === ALTERNATIVE_STATUSES.SELECTED) {
    // $FlowFixMe
    if (toPayment) {
      const isPaymentOk = await dispatch(
        submitForPayment({
          category: SERVICES.SCHEDULE_CHANGE,
          bid,
          price,
          currency,
          orderAction: scheduleChangeOrderAction,
          payload,
        }),
      )
      await dispatch(setLoadingStatus(false))
      return isPaymentOk
    }

    // $FlowFixMe
    promise = selectScheduleChangeAlternative(bid, changeId, payload)
  } else {
    // $FlowFixMe
    promise = manageScheduleChange(bid, changeId, payload)
  }

  promise.catch(error => {
    if (error.data && error.data.detail === "price change") {
      dispatch(bookingActions.openModal(MODALS.SCHEDULE_CHANGE_ALTERNATIVE_EXPIRED))
    }
  })

  dispatch(({ type: INVALIDATE_START }: BookingInvalidateStartAction))
  const ok = await dispatch({
    type: ACTIONS.REQUEST_RESOLUTION,
    action: ACTIONS.REQUEST_RESOLUTION,
    payload,
    promise,
    _track: {
      ...payload,
      ..._track,
    },
  })
  dispatch(({ type: INVALIDATE_END }: BookingInvalidateEndAction))

  if (ok) {
    // if it's REFUND, close the modal
    if (status === ALTERNATIVE_STATUSES.REFUNDED) {
      dispatch(closeScheduleChangesModal())
    }
  }

  return ok // return true for analytics
}

// eslint-disable-next-line flowtype/no-weak-types
export const closeScheduleChanges = () => (dispatch: Function, getState: Function) => {
  const bid = bookingSelectors.bidSelector(getState())
  dispatch(closeScheduleChangesModal())
  dispatch(bookingActions.reloadBooking(bid))
}

export const toggleConfirmRefund = (isConfirmRefundOpen: boolean) => ({
  type: ACTIONS.TOGGLE_CONFIRM_REFUND,
  payload: Map({ isConfirmRefundOpen }),
})

export const isShotEmergencyExpiredSelector: $FlowFixMe = createSelector(
  [
    bookingSelectors.bookingDataSelector,
    bookingSelectors.scheduleChangeIsGuaranteeValidSelector,
    bookingSelectors.shotEmergencyAirlineNamesSelector,
  ],
  (booking: Booking, isGuaranteeValid: boolean, emergencyAirlineNames: List<IataCode>): boolean =>
    !emergencyAirlineNames.isEmpty() &&
    booking.status !== BOOKING_STATUSES.REFUNDED &&
    (!isGuaranteeValid || !booking.journey.endsInFuture()),
)

export const scheduleChangesConditionSelector: $FlowFixMe = createSelector(
  [
    bookingSelectors.scheduleChangeActionSelector,
    bookingSelectors.scheduleChangeFlightsCheckedSelector,
    bookingSelectors.scheduleChangeIsGuaranteeValidSelector,
    hasAlternativesSelector,
    bookingSelectors.scheduleChangeRefundSelector,
    bookingSelectors.scheduleChangesCaseSelector,
    isViewAlernativesSelector,
    isShotEmergencyExpiredSelector,
  ],
  (
    action,
    flightsChecked,
    isGuaranteeValid,
    hasAlternatives,
    scheduleChangeRefund,
    scheduleChangeCase,
    isViewAlternatives,
    isShotEmergencyExpired,
  ) =>
    scheduleChangesConditions({
      action,
      flightsChecked,
      isGuaranteeValid,
      hasAlternatives,
      isRefund:
        Boolean(scheduleChangeRefund.get("amount")) || scheduleChangeRefund.get("refundForm"),
      scheduleChangeCase,
      isViewAlternatives,
      isShotEmergencyExpired,
    }),
)

function changeJourney(part: Part, scheduleChange: Map<any, any>) {
  const partID = part.id

  const change = scheduleChange.getIn(["changes", partID])
  let p = part

  if (change) {
    p = p.setIn(["departure", "updateWhen"], change.getIn(["departure", "when"]))
    p = p.setIn(["arrival", "updateWhen"], change.getIn(["arrival", "when"]))
  }

  return p
}

export function scheduleChangeJourney(journey: JourneyType, scheduleChange: Map<any, any>) {
  return journey.set(
    "trips",
    journey.trips.map(trip =>
      trip
        .set("parts", trip.parts.map(part => changeJourney(part, scheduleChange)))
        .set("flights", trip.parts.map(part => changeJourney(part, scheduleChange))),
    ),
  )
}

export const nextDelayedFlightDataSelector: $FlowFixMe = createSelector(
  [
    bookingSelectors.currentBookingJourneySelector,
    bookingSelectors.scheduleChangeFlightChangesSelector,
    bookingSelectors.currentBookingScheduleChangeSelector,
    bookingSelectors.flightsHistorySelector,
  ],
  (originalJourney: Journey, changes, scheduleChanges, flightsHistory) => {
    // find first flight after from now until which hasn't departed yet
    const journey = scheduleChanges.get("scheduleChangeProcessed")
      ? flightsHistory.last()
      : originalJourney

    // filter out changes
    const changesIDs = changes.keySeq()

    // filter out flights with changes
    const flightsWithChanges = journey.getFlights().filter(flight => changesIDs.includes(flight.id))

    const firstFlightWithMinorDelay =
      flightsWithChanges.find((flight: Flight) =>
        changes.getIn([flight.id, "departure", "when", "utc"]).isAfter(),
      ) || flightsWithChanges.first()

    const { value: delayDuration } = helpers.getFlightDelayDuration(
      firstFlightWithMinorDelay,
      changes.get(firstFlightWithMinorDelay.id),
    )

    return {
      from: firstFlightWithMinorDelay.getFrom(),
      to: firstFlightWithMinorDelay.getTo(),
      delayDuration,
    }
  },
)

export const openRefundForm = () => (
  dispatch: Function, // eslint-disable-line flowtype/no-weak-types
) => {
  dispatch(closeScheduleChangesModal())
  dispatch(bookingActions.openModal(MODALS.REFUND))
  dispatch(setReasonAction(REASONS.BOOKING_CANCELLATION))
  dispatch(changeRefundView(VIEWS.GENERAL))
}
